import { useState } from 'react';
import { useAuditLog } from '@/hooks/useDataVault';
import type { AuditLogItem } from '@/api/dataVault';

interface Props {
  onClose: () => void;
}

function ExportTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    csv: 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50',
    xlsx: 'bg-blue-900/30 text-blue-400 border-blue-800/50',
    geojson: 'bg-purple-900/30 text-purple-400 border-purple-800/50',
    bulk_zip: 'bg-amber-900/30 text-amber-400 border-amber-800/50',
    pdf_report: 'bg-red-900/30 text-red-400 border-red-800/50',
  };
  const labels: Record<string, string> = {
    csv: 'CSV',
    xlsx: 'EXCEL',
    geojson: 'GEOJSON',
    bulk_zip: 'BULK ZIP',
    pdf_report: 'PDF',
  };
  const style = styles[type] || 'bg-mono-800 text-mono-400 border-mono-700';
  const label = labels[type] || type.toUpperCase();
  return (
    <span className={`px-1.5 py-0.5 text-[9px] tracking-wider rounded border ${style}`}>
      {label}
    </span>
  );
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function AuditHistoryPanel({ onClose }: Props) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAuditLog(page, 15, true);

  return (
    <div className="border border-mono-700 rounded-lg bg-mono-900/80 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-medium text-mono-200 tracking-widest">EXPORT HISTORY</h3>
        <button
          onClick={onClose}
          className="text-[10px] text-mono-500 hover:text-mono-300 tracking-wider transition-colors"
        >
          CLOSE
        </button>
      </div>

      {isLoading && !data && (
        <div className="text-mono-500 text-[11px] tracking-wider py-6 text-center">
          LOADING AUDIT LOG...
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="text-mono-600 text-[11px] tracking-wider py-6 text-center">
          NO EXPORTS RECORDED YET
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="border border-mono-800 rounded overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-mono-800 bg-mono-900/50">
                  <th className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest font-medium">TYPE</th>
                  <th className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest font-medium">USER</th>
                  <th className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest font-medium">DATE</th>
                  <th className="px-3 py-2 text-right text-[10px] text-mono-500 tracking-widest font-medium">ROWS</th>
                  <th className="px-3 py-2 text-right text-[10px] text-mono-500 tracking-widest font-medium">SIZE</th>
                  <th className="px-3 py-2 text-left text-[10px] text-mono-500 tracking-widest font-medium">TABS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-mono-800/50">
                {data.items.map((item: AuditLogItem) => (
                  <tr key={item.id} className="hover:bg-mono-800/30 transition-colors">
                    <td className="px-3 py-2">
                      <ExportTypeBadge type={item.export_type} />
                    </td>
                    <td className="px-3 py-2 text-[11px] text-mono-300">{item.user_email || '--'}</td>
                    <td className="px-3 py-2 text-[11px] text-mono-400">{formatDate(item.created_at)}</td>
                    <td className="px-3 py-2 text-[11px] text-mono-400 text-right">
                      {item.row_count !== null ? item.row_count.toLocaleString() : '--'}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-mono-400 text-right">
                      {formatBytes(item.file_size_bytes)}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-mono-500">{item.tabs_included || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-[10px] text-mono-500 tracking-wider">
                PAGE {data.page} OF {data.total_pages} ({data.total} TOTAL)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 text-[10px] tracking-wider text-mono-400 hover:text-mono-200 bg-mono-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  PREV
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.total_pages}
                  className="px-3 py-1 text-[10px] tracking-wider text-mono-400 hover:text-mono-200 bg-mono-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
